import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2, 
  FolderOpen,
  Palette
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    color: '#2E5E99',
  });

  // Colors
  const colors = {
    dark: '#0D2440',
    blue: '#2E5E99',
    gray: '#5A7A9B',
    border: '#C8D8E8',
    bg: '#E7F0FA',
  };

  // Preset colors
  const presetColors = [
    '#2E5E99', '#0D2440', '#5A7A9B', '#10B981', 
    '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Fehler beim Laden der Kategorien');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      color: '#2E5E99',
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/admin/categories`, formData);
      toast.success('Kategorie erfolgreich erstellt');
      setIsCreateOpen(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Kategorie');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const updateData = { name: formData.name, color: formData.color };
      await axios.put(`${API}/admin/categories/${selectedCategory.id}`, updateData);
      toast.success('Kategorie erfolgreich aktualisiert');
      setIsEditOpen(false);
      setSelectedCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren der Kategorie');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    
    try {
      await axios.delete(`${API}/admin/categories/${selectedCategory.id}`);
      toast.success('Kategorie erfolgreich gelöscht');
      setIsDeleteOpen(false);
      setSelectedCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen der Kategorie');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (category) => {
    setSelectedCategory(category);
    setFormData({
      id: category.id,
      name: category.name,
      color: category.color || '#2E5E99',
    });
    setIsEditOpen(true);
  };

  const openDeleteModal = (category) => {
    setSelectedCategory(category);
    setIsDeleteOpen(true);
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: colors.dark }}>Kategorien</h1>
            <p className="mt-1" style={{ color: colors.gray }}>
              Verwalten Sie Service-Kategorien
            </p>
          </div>
          <Button 
            onClick={() => { resetForm(); setIsCreateOpen(true); }} 
            data-testid="create-category-button"
            style={{ background: colors.dark }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Neue Kategorie
          </Button>
        </div>

        {/* Categories Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: colors.blue }} />
          </div>
        ) : categories.length === 0 ? (
          <Card className="bg-white" style={{ borderColor: colors.border }}>
            <CardContent className="py-12 text-center">
              <FolderOpen className="h-12 w-12 mx-auto mb-4" style={{ color: colors.gray }} />
              <h3 className="text-lg font-medium" style={{ color: colors.dark }}>Keine Kategorien vorhanden</h3>
              <p className="mt-2" style={{ color: colors.gray }}>
                Erstellen Sie die erste Kategorie
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((category) => (
              <Card 
                key={category.id} 
                className="bg-white"
                style={{ borderColor: colors.border }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ background: category.color || colors.blue }}
                    >
                      <FolderOpen className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate" style={{ color: colors.dark }}>
                        {category.name}
                      </h3>
                      <p className="text-xs" style={{ color: colors.gray }}>{category.id}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: colors.border }}>
                    <div className="flex items-center gap-1">
                      <Palette className="h-3 w-3" style={{ color: colors.gray }} />
                      <span className="text-xs font-mono" style={{ color: colors.gray }}>
                        {category.color || '#2E5E99'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEditModal(category)}
                        data-testid={`edit-category-${category.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600 hover:text-red-700"
                        onClick={() => openDeleteModal(category)}
                        data-testid={`delete-category-${category.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Category Dialog */}
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ color: colors.dark }}>Neue Kategorie erstellen</DialogTitle>
              <DialogDescription style={{ color: colors.gray }}>
                Erstellen Sie eine neue Service-Kategorie.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="create-id">Kategorie-ID</Label>
                  <Input
                    id="create-id"
                    value={formData.id}
                    onChange={(e) => setFormData({ ...formData, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    placeholder="z.B. meine_kategorie"
                    required
                    data-testid="create-category-id"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-name">Name</Label>
                  <Input
                    id="create-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Kategorie-Name"
                    required
                    data-testid="create-category-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Farbe</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                      data-testid="create-category-color"
                    />
                    <Input
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      placeholder="#2E5E99"
                      className="flex-1 font-mono"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {presetColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className="w-8 h-8 rounded-lg border-2 transition-all hover:scale-110"
                        style={{ 
                          background: color,
                          borderColor: formData.color === color ? colors.dark : 'transparent'
                        }}
                        onClick={() => setFormData({ ...formData, color })}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={isSubmitting} data-testid="submit-create-category" style={{ background: colors.dark }}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Erstellen
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Category Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ color: colors.dark }}>Kategorie bearbeiten</DialogTitle>
              <DialogDescription style={{ color: colors.gray }}>
                Ändern Sie die Kategorie-Informationen.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEdit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-id">Kategorie-ID</Label>
                  <Input
                    id="edit-id"
                    value={formData.id}
                    disabled
                    className="opacity-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Name</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Kategorie-Name"
                    required
                    data-testid="edit-category-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Farbe</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                      data-testid="edit-category-color"
                    />
                    <Input
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      placeholder="#2E5E99"
                      className="flex-1 font-mono"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {presetColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className="w-8 h-8 rounded-lg border-2 transition-all hover:scale-110"
                        style={{ 
                          background: color,
                          borderColor: formData.color === color ? colors.dark : 'transparent'
                        }}
                        onClick={() => setFormData({ ...formData, color })}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={isSubmitting} data-testid="submit-edit-category" style={{ background: colors.dark }}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Kategorie löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie die Kategorie "{selectedCategory?.name}" wirklich löschen? 
                Services mit dieser Kategorie behalten den bisherigen Kategorienamen.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={isSubmitting}
                data-testid="confirm-delete-category"
              >
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default AdminCategories;
